import { useMemo } from 'react';
import { useTranslation } from '../../../lib/i18n';
import type { CalendarView } from '../../../lib/calendar/calendar-dates';
import type { CalendarItem } from '../../../lib/calendar/calendar-item';
import { formatSpan, formatWhen, type CalendarFormatParts } from '../../../lib/calendar/calendar-format';

export function calendarLocale(appLanguage: string | undefined, deviceLanguage: string | undefined): string | undefined {
  const language = appLanguage?.split('-')[0];
  if (!language) return deviceLanguage;
  try {
    const region = deviceLanguage ? new Intl.Locale(deviceLanguage).region : undefined;
    return region ? `${language}-${region}` : appLanguage;
  } catch {
    return appLanguage;
  }
}

// The calendar's dates and times in the app language, device region, and business timezone.
export function useCalendarFormat(timeZone: string) {
  const { i18n } = useTranslation();
  const locale = calendarLocale(i18n.language || undefined, typeof navigator === 'undefined' ? undefined : navigator.language);
  return useMemo(() => {
    const time = new Intl.DateTimeFormat(locale, { hour: 'numeric', minute: '2-digit', timeZone });
    const hour = new Intl.DateTimeFormat(locale, { hour: 'numeric', timeZone });
    const weekday = new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone });
    const weekdayLong = new Intl.DateTimeFormat(locale, { weekday: 'long', timeZone });
    const dayMonth = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', timeZone });
    const fullDay = new Intl.DateTimeFormat(locale, { weekday: 'long', day: 'numeric', month: 'long', timeZone });
    const monthYear = new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric', timeZone });
    const range = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', year: 'numeric', timeZone });
    const dayNumber = new Intl.DateTimeFormat(locale, { day: 'numeric', timeZone });
    const parts: CalendarFormatParts = {
      fullDay: (at) => fullDay.format(at),
      time: (at) => time.format(at),
      timeRange: (start, end) => time.formatRange(start, end),
      price: (amount, currency) => new Intl.NumberFormat(locale, { style: 'currency', currency }).format(Number(amount)),
    };
    return {
      parts,
      locale,
      timeZone,
      time: (at: number) => time.format(at),
      hour: (at: number) => hour.format(at),
      weekday: (at: number) => weekday.format(at),
      weekdayLong: (at: number) => weekdayLong.format(at),
      dayMonth: (at: number) => dayMonth.format(at),
      dayNumber: (at: number) => dayNumber.format(at),
      fullDay: (at: number) => fullDay.format(at),
      /** The toolbar's title for what the view shows. */
      title: (view: CalendarView, days: number[], anchor: number) => {
        if (view === 'month') return monthYear.format(anchor);
        if (view === 'day') return fullDay.format(days[0]);
        return range.formatRange(days[0], days[days.length - 1]);
      },
      /** "Tuesday, October 21, 2:00 – 3:00 PM": a stretch of time picked on the grid, or just its day. */
      when: (start: number, end?: number) => formatWhen(parts, start, end),
      /** "10:00 – 11:30". */
      timeRange: (start: number, end: number) => time.formatRange(start, end),
      /** "10:00 – 11:30", or the one time for a point in time. */
      span: (item: CalendarItem) => formatSpan(parts, item),
    };
  }, [locale, timeZone]);
}

export type CalendarFormat = ReturnType<typeof useCalendarFormat>;
